import { useEffect, useRef, useState } from "react"
import { useNavigate } from "@tanstack/react-router"

import { cn } from "../../lib/utils"
import { useAuth } from "../../hooks/use-auth"
import { useLogout } from "../../services/use-auth"

export function UserMenu({ compact = false }: { compact?: boolean }) {
  const { user } = useAuth()
  const logout = useLogout()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) {
      return
    }
    function onPointer(e: PointerEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setOpen(false)
      }
    }
    document.addEventListener("pointerdown", onPointer)
    document.addEventListener("keydown", onKey)
    return () => {
      document.removeEventListener("pointerdown", onPointer)
      document.removeEventListener("keydown", onKey)
    }
  }, [open])

  if (!user) {
    return null
  }

  const label = user.name ?? user.email ?? `user #${user.id}`
  const initials = getInitials(label)

  function handleSignOut() {
    setOpen(false)
    logout.mutate(undefined, {
      onSettled: () => {
        void navigate({ to: "/login" })
      },
    })
  }

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Open user menu for ${label}`}
        className={cn(
          "flex h-8 items-center gap-2 rounded-full px-1.5 text-sm font-medium text-foreground/80",
          compact ? "w-8 justify-center p-0" : "w-full min-w-0",
          "hover:bg-foreground/5 hover:text-foreground",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        )}
      >
        <span className="flex size-6 items-center justify-center rounded-full bg-primary text-[11px] font-semibold text-primary-foreground">
          {initials}
        </span>

        {!compact ? <span className="min-w-0 truncate">{label}</span> : null}
      </button>

      {open ? (
        <div
          role="menu"
          className="motion-floating-surface absolute bottom-full left-0 z-50 mb-2 w-56 overflow-hidden rounded-xl border border-border bg-popover shadow-floating"
        >
          <div className="border-b border-border px-3 py-2">
            <div className="truncate text-sm font-medium text-foreground">{label}</div>

            {user.email && user.name ? (
              <div className="truncate text-xs text-muted-foreground">{user.email}</div>
            ) : null}

            <div className="mt-0.5 text-xs capitalize text-muted-foreground/60">{user.role}</div>
          </div>

          <button
            type="button"
            role="menuitem"
            onClick={handleSignOut}
            disabled={logout.isPending}
            className={cn(
              "block w-full px-3 py-2 text-left text-sm text-foreground",
              "hover:bg-muted/60",
              "disabled:pointer-events-none disabled:opacity-50"
            )}
          >
            {logout.isPending ? "Signing out…" : "Sign out"}
          </button>
        </div>
      ) : null}
    </div>
  )
}

function getInitials(label: string): string {
  const parts = label
    .trim()
    .split(/[\s@._-]+/)
    .filter(Boolean)
  const [first, second] = parts
  if (!first) {
    return "?"
  }
  if (!second) {
    return first.slice(0, 2).toUpperCase()
  }
  return (first.charAt(0) + second.charAt(0)).toUpperCase()
}
